// Calendário do Pipeline — a agenda comercial do mês.
//
// Nasceu (L16b, 2026-06-03) mostrando só a abertura das licitações. Redesenho de
// 01/10/2026: passa a mostrar TODA data que o pipeline já tem, cada tipo com a
// sua cor e um botão para ligar/desligar:
//
//  • abertura de licitação (urgência pela proximidade, como antes);
//  • próximo passo agendado de um negócio (atrasado fica vermelho);
//  • proposta enviada (`data_envio`);
//  • venda fechada (`data_fechamento` de negócio ganho) — com o valor;
//  • negócio perdido (desligado por padrão).
//
// E o cabeçalho resume o mês que está na tela: quanto se vendeu, quantas
// propostas saíram, quantas aberturas e passos há pela frente.
//
// ⚠️ O que este calendário NÃO é: previsão de fechamento. Os CRMs de referência
// têm uma vista de "forecast" que distribui os negócios abertos pelo mês em que
// devem fechar — e isso exige um campo "previsão de fechamento" que o negócio
// não tem. Criar o campo é decisão de negócio, não de tela.
import { useMemo, useState } from 'react';
import { Calendar, Typography, Tooltip, Card, Flex, Tag, Popover } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Proposta } from './types';
import { urgenciaAbertura, diasAte, fmt } from './utils';
import { ehAberto, valorDoNegocio, situacaoDoProximoPasso, type ProximoPasso } from './saudeNegocio';

const { Text } = Typography;

type Tipo = 'abertura' | 'passo' | 'envio' | 'ganho' | 'perdido';
type Marca = { tipo: Tipo; dia: string; p: Proposta; cor: string; negrito: boolean; rotulo: string };

const TIPOS: { tipo: Tipo; nome: string; cor: string }[] = [
  { tipo: 'abertura', nome: 'Abertura de licitação', cor: '#722ED1' },
  { tipo: 'passo',    nome: 'Próximo passo',         cor: '#0930D0' },
  { tipo: 'envio',    nome: 'Proposta enviada',      cor: '#B07500' },
  { tipo: 'ganho',    nome: 'Venda fechada',         cor: '#0a7f4a' },
  { tipo: 'perdido',  nome: 'Perdido',               cor: '#8B3A3A' },
];
const COR: Record<Tipo, string> = Object.fromEntries(TIPOS.map(t => [t.tipo, t.cor])) as Record<Tipo, string>;

const diaDe = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

// O nome do mês sai do navegador, não do dayjs: o dayjs do projeto não tem o
// idioma pt-BR carregado e escreveria "October".
const nomeDoMes = (m: Dayjs) => {
  const t = m.toDate().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function CalendarioPipeline({ propostas, passos, onAbrir }: {
  propostas: Proposta[];
  passos: Record<number, ProximoPasso>;
  onAbrir: (p: Proposta) => void;
}) {
  const [mes, setMes] = useState<Dayjs>(() => dayjs());
  const [ligados, setLigados] = useState<Set<Tipo>>(() => new Set<Tipo>(['abertura', 'passo', 'envio', 'ganho']));
  const alternar = (t: Tipo) => setLigados(prev => {
    const n = new Set(prev);
    if (n.has(t)) n.delete(t); else n.add(t);
    return n;
  });

  const marcas = useMemo(() => {
    const lista: Marca[] = [];
    for (const p of propostas) {
      const aberto = ehAberto(p.status);
      if (p.origem === 'licitacao' && p.data_abertura && aberto) {
        const urg = urgenciaAbertura(diasAte(p.data_abertura));
        lista.push({ tipo: 'abertura', dia: diaDe(p.data_abertura), p, cor: urg.cor, negrito: urg.bold,
          rotulo: `Abertura do edital ${urg.label}` });
      }
      const passo = passos[p.id];
      if (aberto && passo?.proximo) {
        // Atrasado/hoje decididos pelo dia LOCAL (saudeNegocio), não pela
        // bandeira do servidor, que conta em UTC.
        const sit = situacaoDoProximoPasso(p, passo);
        lista.push({ tipo: 'passo', dia: diaDe(passo.proximo), p,
          cor: sit === 'vencido' ? '#B91626' : COR.passo, negrito: sit === 'vencido' || sit === 'hoje',
          rotulo: sit === 'vencido' ? 'Próximo passo atrasado' : sit === 'hoje' ? 'Próximo passo para hoje' : 'Próximo passo agendado' });
      }
      if (p.data_envio) {
        lista.push({ tipo: 'envio', dia: diaDe(p.data_envio), p, cor: COR.envio, negrito: false,
          rotulo: 'Proposta enviada' });
      }
      if (p.status === 'ganho' && p.data_fechamento) {
        lista.push({ tipo: 'ganho', dia: diaDe(p.data_fechamento), p, cor: COR.ganho, negrito: true,
          rotulo: `Venda fechada · ${fmt(valorDoNegocio(p))}` });
      }
      if (p.status === 'perdido' && p.data_fechamento) {
        lista.push({ tipo: 'perdido', dia: diaDe(p.data_fechamento), p, cor: COR.perdido, negrito: false,
          rotulo: 'Negócio perdido' });
      }
    }
    return lista;
  }, [propostas, passos]);

  const porDia = useMemo(() => {
    const map = new Map<string, Marca[]>();
    for (const m of marcas) {
      if (!ligados.has(m.tipo)) continue;
      if (!map.has(m.dia)) map.set(m.dia, []);
      map.get(m.dia)!.push(m);
    }
    return map;
  }, [marcas, ligados]);

  // O resumo é do mês QUE ESTÁ NA TELA, e conta todos os tipos — desligar um
  // tipo esconde as marcas, não muda o que aconteceu no mês.
  const chaveMes = mes.format('YYYY-MM');
  const doMes = marcas.filter(m => m.dia.startsWith(chaveMes));
  const conta = (t: Tipo) => doMes.filter(m => m.tipo === t).length;
  const vendido = doMes.filter(m => m.tipo === 'ganho').reduce((a, m) => a + valorDoNegocio(m.p), 0);

  const linha = (m: Marca, completo = false) => (
    <Tooltip key={m.tipo + m.p.id} title={completo ? undefined : `${m.rotulo} — ${m.p.titulo}${m.p.cliente_nome ? ' · ' + m.p.cliente_nome : ''}`}>
      <div onClick={(e) => { e.stopPropagation(); onAbrir(m.p); }}
        role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') onAbrir(m.p); }}
        style={{
          cursor: 'pointer', fontSize: completo ? 12 : 10.5, lineHeight: completo ? '18px' : '15px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          color: '#0E1433', fontWeight: m.negrito ? 600 : 400,
          padding: '0 4px 0 6px', borderLeft: `3px solid ${m.cor}`, borderRadius: 2,
          background: completo ? undefined : `${m.cor}14`, marginBottom: 2,
          maxWidth: completo ? 360 : undefined,
        }}>
        {completo && <span style={{ color: '#5A6378' }}>{m.rotulo} — </span>}
        {m.p.titulo}
      </div>
    </Tooltip>
  );

  const cellRender = (date: Dayjs, info: { type: string }) => {
    if (info.type !== 'date') return null;
    const lista = porDia.get(date.format('YYYY-MM-DD')) || [];
    if (!lista.length) return null;
    const mostra = lista.slice(0, 3);
    return (
      <div style={{ overflow: 'hidden' }}>
        {mostra.map(m => linha(m))}
        {lista.length > 3 && (
          <Popover trigger="click" placement="rightTop"
            title={date.format('DD/MM/YYYY')}
            content={<div style={{ maxHeight: 320, overflowY: 'auto' }}>{lista.map(m => linha(m, true))}</div>}>
            <button type="button" onClick={e => e.stopPropagation()}
              style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5, color: '#0930D0' }}>
              +{lista.length - 3} no dia
            </button>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '10px 12px' } }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
          <Flex gap={18} wrap="wrap" align="baseline">
            <Text strong style={{ fontSize: 13 }}>{nomeDoMes(mes)}</Text>
            <Text style={{ fontSize: 12, color: '#5A6378' }}>
              Vendido <b style={{ color: '#0a7f4a' }}>{fmt(vendido)}</b>
              {conta('ganho') > 0 && <> em {conta('ganho')} venda{conta('ganho') === 1 ? '' : 's'}</>}
            </Text>
            <Text style={{ fontSize: 12, color: '#5A6378' }}><b style={{ color: '#0E1433' }}>{conta('envio')}</b> proposta{conta('envio') === 1 ? '' : 's'} enviada{conta('envio') === 1 ? '' : 's'}</Text>
            <Text style={{ fontSize: 12, color: '#5A6378' }}><b style={{ color: '#0E1433' }}>{conta('abertura')}</b> abertura{conta('abertura') === 1 ? '' : 's'} de licitação</Text>
            <Text style={{ fontSize: 12, color: '#5A6378' }}><b style={{ color: '#0E1433' }}>{conta('passo')}</b> próximo{conta('passo') === 1 ? '' : 's'} passo{conta('passo') === 1 ? '' : 's'}</Text>
          </Flex>
          <Flex gap={6} wrap="wrap">
            {TIPOS.map(t => (
              <Tag.CheckableTag key={t.tipo} checked={ligados.has(t.tipo)} onChange={() => alternar(t.tipo)}
                style={{ margin: 0, fontSize: 11, border: '1px solid #E3E6EE' }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 5,
                  background: ligados.has(t.tipo) ? '#fff' : t.cor }} />
                {t.nome}
              </Tag.CheckableTag>
            ))}
          </Flex>
        </Flex>
      </Card>
      <Card size="small" styles={{ body: { padding: 8 } }}>
        <Calendar value={mes} onPanelChange={(v) => setMes(v)} onSelect={(v) => setMes(v)}
          cellRender={cellRender as any} />
      </Card>
    </div>
  );
}
